import { Person } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getCourseLevelLabel } from "../../constants/courseLevels";

const CourseCard = ({
  teacherCourseId,
  courseTitle,
  parentTitle,
  teacherFirstName,
  teacherLastName,
  startDate,
  daysPerWeek,
  duration,
  enrolledStudents = 0,
  maxStudents = 0,
  enrolledView = false,
}) => {
  const { t, i18n } = useTranslation();
  const { enrolledCoursesIds } = useSelector(
    (state) => state.enrolledStudentCourses || []
  );
  const isArabic = i18n.language === "ar";
  const language = i18n.language?.startsWith("ar") ? "ar" : i18n.language?.startsWith("de") ? "de" : "en";
  const availableLabel = {
    en: "Available course",
    de: "Buchbarer Kurs",
    ar: "دورة متاحة للحجز",
  }[language];
  const enrolledLabel = {
    en: "Enrolled course",
    de: "Eingeschriebener Kurs",
    ar: "دورة مسجل بها",
  }[language];
  const teacherName = `${teacherFirstName} ${teacherLastName}`;
  const formattedStartDate = startDate
    ? new Date(startDate).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  const safeEnrolled = Number(enrolledStudents) || 0;
  const safeMax = Number(maxStudents) || 0;
  const isFull = safeMax > 0 && safeEnrolled >= safeMax;
  const remaining = Math.max(0, safeMax - safeEnrolled);
  const isAlmostFull = !isFull && safeEnrolled > 0 && remaining <= 2;
  const displayLevel = parentTitle || courseTitle;
  const courseTitleLabel = getCourseLevelLabel(t, courseTitle);
  const parentTitleLabel = getCourseLevelLabel(t, parentTitle);
  const shouldShowLevelPrefix = /^[A-C][0-9]/i.test(String(courseTitle || ""));
  const levelImages = {
    A1: "/a1-course.png",
    A2: "/a2-course.png",
    B1: "/b1-course.png",
    B2: "/b2-course.png",
    C1: "/c1-course.png",
    Konversationskurs: "/konversationskurs-course.png",
    Grammatikkurs: "/grammatikkurs-course.png",
  };
  const courseImage = levelImages[displayLevel] || levelImages[courseTitle] || "/login-image.jpg";

  const statusNode = (() => {
    if (isFull) {
      return (
        <Typography color="error.main" sx={{ fontSize: "0.75rem" }}>
          🚫 {t("courseDetails.fullyBooked")}
        </Typography>
      );
    }

    if (safeEnrolled === 0) {
      return (
        <Typography color="orange" sx={{ fontSize: "0.75rem" }}>
          ⚡ {t("courseDetails.beFirst")}
        </Typography>
      );
    }

    if (isAlmostFull) {
      return (
        <Typography color="orange" sx={{ fontSize: "0.75rem" }}>
          ⚡ {t("courseDetails.almostFull")}
        </Typography>
      );
    }

    if (safeMax > 0 && remaining > 0) {
      return (
        <Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
          {t("courseDetails.spotsLeft", { count: remaining })}
        </Typography>
      );
    }

    return null;
  })();

  return (
    <Box
      component={Link}
      to={
        enrolledView || enrolledCoursesIds?.includes(teacherCourseId)
          ? `/dashboard/enrolled-course/${teacherCourseId}`
          : `/courses/${teacherCourseId}`
      }
      sx={{
        backgroundColor: "#FFFDF3",
        width: "100%",
        minWidth: { xs: "auto", sm: 300, md: 340 },
        borderRadius: "10px",
        height: "100%",
        minHeight: { xs: "320px", sm: "400px", md: "440px" },
        direction: isArabic ? "rtl" : "ltr",
        overflow: "hidden",
        cursor: "pointer",
        textDecoration: "none",
        border: "2px solid #FFD600",
        display: "block",
        position: "relative",
        color: "inherit",
        px: { xs: "12px", sm: "18px", md: "22px" },
        boxSizing: "border-box",
        transition:
          "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          transform: "translateY(-10px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          borderColor: "#FFD600",
          color: "inherit",
        },
        "&:visited, &:active": {
          color: "inherit",
        },
      }}
    >
      <Box
        component="img"
        src={courseImage}
        alt="course"
        onError={(event) => {
          event.currentTarget.src = "/login-image.jpg";
        }}
        sx={{
          paddingTop: { xs: "12px", sm: "16px", md: "22px" },
          width: "100%",
          height: { xs: 180, sm: 220, md: 250 },
          objectFit: "cover",
        }}
      />

      <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
        <Chip
          label={enrolledView ? enrolledLabel : availableLabel}
          size="small"
          sx={{ mb: 1.25, bgcolor: "#FFD600", color: "#111", fontWeight: 900 }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold" sx={{ fontSize: "1rem" }}>
            {shouldShowLevelPrefix ? `${t("level")} ` : ""}
            {courseTitleLabel}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign={isArabic ? "right" : "left"}
            sx={{ fontSize: "0.85rem" }}
          >
            {t("starts")} {formattedStartDate}
          </Typography>
        </Box>

        {parentTitle && parentTitle !== courseTitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign={isArabic ? "right" : "left"}
            sx={{ mt: 0.5, fontSize: "0.9rem" }}
          >
            {parentTitleLabel}
          </Typography>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
          mt={{ xs: 0.5, sm: 1 }}
          flexDirection={isArabic ? "row-reverse" : "row"}
        >
          <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>
            {daysPerWeek} {t("courseDetails.daysPerWeek")}
          </Typography>
          <Typography
            sx={{
              display: "flex",
              flexDirection: isArabic ? "row-reverse" : "row",
              gap: 1,
            }}
            variant="body2"
            fontSize="1rem"
          >
            <Person fontSize="small" sx={{ verticalAlign: "middle", ml: -0.5 }} />
            {teacherName}
          </Typography>
        </Box>

        {/* Capacity is relevant on the public catalog, not in the student's enrolled-course list. */}
        {!enrolledView && <Box
          sx={{
            mt: 1,
            fontSize: "0.9rem",
            display: "flex",
            flexDirection: "row",
            direction: "ltr",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            width: "100%",
          }}
        >
          <Typography
            fontWeight="bold"
            color={isFull ? "error.main" : "#333"}
            sx={{
              fontSize: "0.85rem",
              textAlign: isArabic ? "right" : "left",
              order: isArabic ? 2 : 1,
              flexGrow: 1,
            }}
          >
            {t("courseDetails.students")}: {safeEnrolled}/{safeMax}
          </Typography>

          <Box
            sx={{
              textAlign: isArabic ? "left" : "right",
              flexShrink: 0,
              order: isArabic ? 1 : 2,
            }}
          >
            {statusNode}
          </Box>
        </Box>}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderTop="1px solid #ddd"
          pt={{ xs: 0.5, sm: 1 }}
          mt={{ xs: 0.5, sm: 1 }}
          fontSize="0.9rem"
          color="#555"
          flexDirection={isArabic ? "row-reverse" : "row"}
        >
          <Typography variant="body3">{t("googleMeet")}</Typography>
          <Typography variant="body3">
            {t("noOfWeeks")} : {duration}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CourseCard;
